/* rv-gallery: the PDP gallery (DESIGN-SYSTEM.md §9.2, §6.3) for sections/rv-main-product.liquid.
   The slides are a native CSS scroll-snap track (swipe, trackpad and arrow keys work without JS, and each slide
   carries its own server-rendered "1 of 5" label). This only adds: the current thumbnail (aria-current), the
   previous / next buttons (desktop), thumbnail clicks, mouse-drag swiping and a click-to-enlarge viewer (2026-10-11).
   No animation beyond the browser's own smooth scroll, which is skipped under prefers-reduced-motion. */
(() => {
  if (customElements.get('rv-gallery')) return;
  const still = window.matchMedia('(prefers-reduced-motion: reduce)');

  class RvGallery extends HTMLElement {
    connectedCallback() {
      if (this.track) return;
      this.track = this.querySelector('[data-rv-track]');
      if (!this.track) return;
      this.slides = [...this.track.querySelectorAll('[data-rv-slide]')];
      this.thumbs = [...this.querySelectorAll('[data-rv-thumb]')];
      this.prev = this.querySelector('[data-rv-prev]');
      this.next = this.querySelector('[data-rv-next]');
      this.i = 0;
      /* slides 2+ start loading once slide 1's image (the LCP) is in (rv-gallery.css, speed 2026-10-11) */
      const ready = () => this.setAttribute('data-rv-ready', '');
      const first = this.slides[0] && this.slides[0].querySelector('img');
      if (!first || first.complete) ready();
      else {
        first.addEventListener('load', ready, { once: true });
        first.addEventListener('error', ready, { once: true });
        setTimeout(ready, 6000);
      }
      if (this.slides.length < 2) return;

      /* the slide that is mostly in view is the current one (works for swipes, keys and clicks alike) */
      this.io = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            if (e.isIntersecting) this.set(this.slides.indexOf(e.target));
          });
        },
        { root: this.track, threshold: 0.6 }
      );
      this.slides.forEach((s) => this.io.observe(s));

      this.onClick = (e) => {
        const t = e.target.closest('[data-rv-thumb], [data-rv-prev], [data-rv-next]');
        if (!t || !this.contains(t) || t.getAttribute('aria-disabled') === 'true') return;
        if (t.hasAttribute('data-rv-thumb')) this.go(+t.dataset.rvThumb);
        else this.go(this.i + (t.hasAttribute('data-rv-next') ? 1 : -1));
      };
      this.addEventListener('click', this.onClick);
      this.set(0);
      this.drag();
    }

    /* Cris, 2026-10-11: "swipe through, even on desktop, by just holding your mouse" and "If you click the photo, I do
       want the photos to expand out or to magnify". A mouse drag scrolls the track (snap off while held, then it settles
       on the next or previous slide); a plain click (or Enter on the focused track) opens the viewer (rv-zoom below). */
    drag() {
      const tr = this.track;
      let x0 = 0, sl0 = 0, moved = false, down = false, i0 = 0;
      tr.addEventListener('dragstart', (e) => e.preventDefault());
      const move = (e) => {
        if (!down) return;
        const dx = e.clientX - x0;
        if (!moved && Math.abs(dx) > 5) { moved = true; tr.classList.add('is-dragging'); }
        if (moved) tr.scrollLeft = sl0 - dx;
      };
      const up = (e) => {
        if (!down) return;
        down = false;
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', up);
        if (!moved) return;
        const dx = e.clientX - x0;
        tr.classList.remove('is-dragging');
        const w = this.slides[0].offsetWidth || 1;
        let n = Math.round(tr.scrollLeft / w);
        if (Math.abs(dx) > 40 && n === i0) n = i0 + (dx < 0 ? 1 : -1);
        this.go(n);
        this.noClick = true;
        setTimeout(() => { this.noClick = false; }, 0);
      };
      tr.addEventListener('pointerdown', (e) => {
        if (e.pointerType !== 'mouse' || e.button !== 0 || this.slides.length < 2) return;
        down = true; moved = false; x0 = e.clientX; sl0 = tr.scrollLeft; i0 = this.i;
        window.addEventListener('pointermove', move);
        window.addEventListener('pointerup', up);
      });
      tr.addEventListener('click', (e) => {
        if (this.noClick) { e.preventDefault(); e.stopPropagation(); return; }
        const slide = e.target.closest('[data-rv-slide]');
        if (slide && slide.querySelector('img')) RvZoom.open(this, this.slides.indexOf(slide));
      }, true);
      tr.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && e.target === tr) RvZoom.open(this, this.i);
      });
    }

    disconnectedCallback() {
      if (this.io) this.io.disconnect();
      if (this.onClick) this.removeEventListener('click', this.onClick);
      this.track = null;
    }

    set(i) {
      if (i < 0 || i >= this.slides.length) return;
      this.i = i;
      this.thumbs.forEach((t, k) => {
        if (k === i) t.setAttribute('aria-current', 'true');
        else t.removeAttribute('aria-current');
      });
      // a long thumbnail column or strip (it scrolls) keeps the current thumbnail in view; only it moves, never the page
      const t = this.thumbs[i];
      const box = t && t.parentElement;
      if (box && (box.scrollHeight > box.clientHeight || box.scrollWidth > box.clientWidth)) {
        const b = box.getBoundingClientRect();
        const r = t.getBoundingClientRect();
        if (box.scrollHeight > box.clientHeight) {
          if (r.top < b.top) box.scrollTop += r.top - b.top;
          else if (r.bottom > b.bottom) box.scrollTop += r.bottom - b.bottom;
        }
        if (box.scrollWidth > box.clientWidth) {
          if (r.left < b.left) box.scrollLeft += r.left - b.left;
          else if (r.right > b.right) box.scrollLeft += r.right - b.right;
        }
      }
      const last = this.slides.length - 1;
      if (this.prev) this.prev.setAttribute('aria-disabled', String(i === 0));
      if (this.next) this.next.setAttribute('aria-disabled', String(i === last));
    }

    go(i) {
      const n = Math.max(0, Math.min(this.slides.length - 1, i));
      this.track.scrollTo({ left: this.slides[n].offsetLeft, behavior: still.matches ? 'auto' : 'smooth' });
      this.set(n);
    }
  }

  /* the viewer: one <dialog> per page, built on first use; the largest image the slide offers, click to magnify at the
     click point, arrows / arrow keys between slides, Esc or a click outside the photo to close */
  const RvZoom = {
    open(gal, i) {
      if (!this.d) this.build();
      this.gal = gal;
      this.show(i);
      if (!this.d.open) {
        this.d.showModal();
        document.documentElement.classList.add('rv-zoom-open');
      }
    },
    build() {
      const d = document.createElement('dialog');
      d.className = 'rv-zoom';
      d.setAttribute('aria-label', 'Image viewer');
      const chev = (p) => `<svg viewBox="0 0 24 24" width="30" height="30" aria-hidden="true" focusable="false"><path d="${p}" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
      d.innerHTML = `<button type="button" class="rv-zoom__close" aria-label="Close">${chev('M6 6l12 12M18 6 6 18')}</button>` +
        `<button type="button" class="rv-zoom__nav rv-zoom__nav--prev" aria-label="Previous image">${chev('M15 5l-7 7 7 7')}</button>` +
        `<figure class="rv-zoom__fig"><img class="rv-zoom__img" alt=""></figure>` +
        `<button type="button" class="rv-zoom__nav rv-zoom__nav--next" aria-label="Next image">${chev('M9 5l7 7-7 7')}</button>`;
      document.body.appendChild(d);
      this.d = d;
      this.img = d.querySelector('.rv-zoom__img');
      d.querySelector('.rv-zoom__close').addEventListener('click', () => d.close());
      d.querySelector('.rv-zoom__nav--prev').addEventListener('click', () => this.show(this.i - 1));
      d.querySelector('.rv-zoom__nav--next').addEventListener('click', () => this.show(this.i + 1));
      d.addEventListener('click', (e) => { if (e.target === d || e.target.classList.contains('rv-zoom__fig')) d.close(); });
      d.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') this.show(this.i - 1);
        if (e.key === 'ArrowRight') this.show(this.i + 1);
      });
      d.addEventListener('close', () => {
        document.documentElement.classList.remove('rv-zoom-open');
        this.img.classList.remove('is-mag');
        if (this.gal) this.gal.go(this.i);
      });
      this.img.addEventListener('click', (e) => {
        const r = this.img.getBoundingClientRect();
        this.img.style.transformOrigin = `${((e.clientX - r.left) / r.width) * 100}% ${((e.clientY - r.top) / r.height) * 100}%`;
        this.img.classList.toggle('is-mag');
      });
    },
    show(i) {
      const slides = this.gal.slides;
      this.i = (i + slides.length) % slides.length;
      const src = slides[this.i].querySelector('img');
      let url = src.currentSrc || src.src, best = 0;
      (src.getAttribute('srcset') || '').split(',').forEach((c) => {
        const [u, w] = c.trim().split(/\s+/);
        const n = parseInt(w, 10) || 0;
        if (u && n > best) { best = n; url = u; }
      });
      this.img.classList.remove('is-mag');
      this.img.src = url;
      this.img.alt = src.alt || '';
      const many = slides.length > 1;
      this.d.querySelectorAll('.rv-zoom__nav').forEach((b) => { b.hidden = !many; });
    },
  };

  customElements.define('rv-gallery', RvGallery);
})();
